import React, { useEffect, useState } from "react"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import qs from "qs"

import { apim } from "@/constants/api"
import { getUserPersona } from "@/utils/helper"
import { getConfig } from "@/constants/config"
import useProductCardi18n from "@/i18n/useProductCardi18n"
import MissingImage from "@/public/images/image-missing.png"
import SliderProductCard from "@/components/ProductCarousel/SliderProductCard"
import ProductCarouselLoader from "@/components/ProductCarousel/ProductCarouselLoader"

const ProductCarousel = props => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState({})
  const persona = getUserPersona()
  const staticTexts = useProductCardi18n({})
  const presetConfigs = {
    Desktop: "$PDPMoreCollectionDesktop$&fmt=webp",
    MobilePortrait: "$PDPMoreCollectionDesktop$&fmt=webp",
    MobileLandscape: "$PDPMoreCollectionDesktop$",
    PotraitCategory: "GBH_DataDrivenVerticalTemplate",
  }
  const swatchUrlKohler = "https://kohler.scene7.com/is/image/PAWEB/"

  useEffect(() => {
    getConfig().then(config => {
      const brandName = config?.general?.siteName ?? ""
      const language = _get(config, "internationalization.language", "en")
      const locale = _get(config, "internationalization.locale", "")
      const currencySign = _get(config, "internationalization.currencySign", "")
      const currencyCode = _get(config, "internationalization.currencyCode", "")
      const { general: { lwAppName = "" } = {} } = config ?? {}

      setInfo({
        locale,
        language,
        currencySign,
        currencyCode,
        brandName,
        lwAppName,
      })
    })
  }, [])

  const fetchProducts = async info => {
    try {
      const fl = [
        "id",
        "slug_s",
        "productName_s",
        "masterSKU_s",
        "Product_Category",
        "ProductBrandName_s",
        "productImages.url_ss",
        "productImageVariantImages.url_ss",
        "SKUColorSwatchFilename_ss",
        "ProductDescriptionProductShort_s",
        `priceStartingAt.${persona}_d`,
        `discountedPriceStartingAt.${persona}_d`,
        `priceList.${persona}.saleOffer_s`,
        "Color.SKU.Details_ss",
        "ProductWebFeatures_ss",
        "ProductWebFeatures_s",
        "ProductExclusive_s",
        "ProductIsExclusive_s",
        "RegionReleaseforShipment_dt",
        "title_s",
        "ProductNarrativeDescription_s",
        `priceList.${persona}.finalPrice_d`,
        "CustomerFacingBrand_s",
        "RegionBrandName_s",
        "productImages.labelWithUrl_ss",
      ]

      const productSkus = props.data.productSku.map(
        product => `"${product.productSkuId}"`
      )

      const res = await apim.get(`/search/plp`, {
        params: {
          fl: fl.join(","),
          q: "*:*",
          fq: [
            `variantList.sku_ss:(${productSkus.join(",")})`,
            `language_s:("en")`,
          ],
          collections: info.lwAppName.toLowerCase(),
          profilename: `profile_${info.lwAppName.toLowerCase()}_General`,
        },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat", encode: false }),
      })

      setProducts(res.data.response.docs)
    } catch (err) {
      console.error("Failed to fetch products:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!_isEmpty(info)) {
      fetchProducts(info)
    }
  }, [info])

  if (loading) {
    return (
      <div>
        <ProductCarouselLoader productSkus={props?.data?.productSku ?? []} />
      </div>
    )
  }

  return (
    <div className="product-carousel-wrapper">
      <div className="product-carousel">
        {products.map(product => {
          const productCardProps = {}
          productCardProps.presetConfigs = presetConfigs
          productCardProps.swatchUrl = swatchUrlKohler

          if (product) {
            // get product images
            if (product["modifiedproductImageVariantImages.url_ss"]) {
              const imageStrig =
                product["modifiedproductImageVariantImages.url_ss"]?.split("|")
              productCardProps.images = imageStrig.length
                ? imageStrig[imageStrig.length - 1]
                : ""
            } else if (product["productImages.url_ss"]) {
              productCardProps.images = product["productImages.url_ss"][0] ?? ""
            } else {
              productCardProps.images = MissingImage?.src
            }

            // get product colors
            if (product["Color.SKU.Details_ss"]) {
              const colors = product["Color.SKU.Details_ss"] ?? []
              productCardProps.colors = colors.filter(item => {
                const color = item.split("|")
                return color[4] === "false" || Date.parse(color[4]) > Date.now()
              })
            }

            // get product web features
            if (product["ProductWebFeatures_s"]) {
              productCardProps.productFeatures =
                [product["ProductWebFeatures_s"]] ?? []
            } else {
              productCardProps.productFeatures =
                product["ProductWebFeatures_ss"] ?? []
            }

            productCardProps.currencySign = info.currencySign
            productCardProps.productCategory = _get(
              product,
              "Product_Category",
              ""
            )
            productCardProps.name = _get(product, "productName_s", "")
            productCardProps.brandName = _get(product, "RegionBrandName_s", "")
            productCardProps.description = _get(product, "title_s", "")
            productCardProps.narrativeDescription = _get(
              product,
              "ProductNarrativeDescription_s",
              ""
            )
            productCardProps.price = _get(
              product,
              `priceStartingAt.${persona}_d`,
              0
            )
            productCardProps.discountedPrice = _get(
              product,
              `discountedPriceStartingAt.${persona}_d`,
              0
            )
            productCardProps.discountPercent = parseInt(
              _get(product, `priceList.${persona}.saleOffer_s`, 0)
            )
            productCardProps.slug = _get(product, "slug_s", "")
            productCardProps.customerBrand = _get(
              product,
              "CustomerFacingBrand_s",
              ""
            )
            let tempColorDetails = _get(product, "Color.SKU.Details_ss", null)
            if (tempColorDetails) {
              if (typeof tempColorDetails !== "string" && tempColorDetails[0]) {
                tempColorDetails = tempColorDetails[0]
              }
              productCardProps.customerFacingSKU =
                tempColorDetails.split("|")[5] || ""
            }
            productCardProps.badgeVal = _get(
              product,
              `priceList.${persona}.saleOffer_s`,
              ""
            )
            productCardProps.globalDiscount =
              productCardProps.price -
              _get(
                product,
                `priceList.${persona}.finalPrice_d`,
                productCardProps.price
              )
            const productCategory =
              productCardProps?.productCategory?.toLowerCase()
            productCardProps.priceType = ""
            if (
              productCategory === "field tiles" ||
              productCategory === "slabs" ||
              productCategory === "mosaics"
            ) {
              productCardProps.priceType = "/" + staticTexts?.sfLabel
            }
            if (productCategory === "trims & moldings") {
              productCardProps.priceType = "/" + staticTexts?.lfLabel
            }
            if (productCategory === "grout") {
              productCardProps.priceType = staticTexts?.boxLabel
            }
          }

          return (
            <div className={"product-carousel__card"} key={product.masterSKU_s}>
              <SliderProductCard
                i18n={staticTexts}
                product={productCardProps}
                currencySign={info.currencySign}
                presetConfigs={presetConfigs}
                swatchUrl={swatchUrlKohler}
                title={product.productName_s}
                brandName={product.ProductBrandName_s}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProductCarousel
